import { faCircleExclamation } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, Typography } from "@mui/material"
import { useEffect } from "react"
import error_mark from "../../../../assets/images/error_mark.svg"
import Checkbox from "../../../../components/Checkbox/Checkbox"
import InputTag from "../../../../components/Input/InputTag"
import CircularLoader from "../../../../components/Loader/CircularLoader"
import MultiSelectDropdown from "../../../../components/MultiSelectDropdown/MultiSelectDropdown"
import RadioGroup from "../../../../components/RadioGroup/RadioGroup"
import { GST_TREATMENT_OPTIONS, MSME_REG_TYPE_OPTIONS, ZONE_OPTIONS } from "../../../../enums/dropdownValues"
import validationError from "../../../../enums/validationError"
import { AccountsTableData, useGetAccountDepartmentQuery, useGetAccountServicesQuery } from "../../AccountsServices"
import { accountIdentifierField } from "../../accountFields"
import styles from "./AddAccounts.module.scss"
import BankDetails from "./BankDetails"
import ContactDetails from "./ContactDetails"
import ShippingAddress from "./shippingAddress"
import useAddAccounts from "./useAddAccounts"

type Props = {
    toggle: () => void
    isShown: boolean
    ticketData?: AccountsTableData
    editFlag?: boolean
    onSuccess?: () => void
    defaultAccountType?: { id: string; title: string }
    disableAccountType?: boolean
}

const AddAccounts = (props: Props) => {
    const { ticketData, editFlag, defaultAccountType, disableAccountType } = props
    const { addStates, addfunctions } = useAddAccounts({
        ticketData,
        editFlag,
        toggle: props.toggle,
        onSuccess: props.onSuccess,
    })
    const identifierField = accountIdentifierField(addStates.addAccountFormValue?.account_type?.id)
    const {
        areAllContactFieldsFilled,
        areAllBillingFieldsFilled,
        resetAddAccountData,
        handleDisable,
        handleAccountTypeChange,
        setVendorDetails,
    } = addfunctions

    useEffect(() => {
        if (defaultAccountType && !editFlag) {
            handleAccountTypeChange(defaultAccountType)
        }
    }, [defaultAccountType, editFlag])
    const { data: serviceData } = useGetAccountServicesQuery(undefined, {
        skip: addStates?.addAccountFormValue?.account_type?.id !== "Vendor",
    })
    const { data: departmentData } = useGetAccountDepartmentQuery(undefined, {
        skip: addStates?.addAccountFormValue?.account_type?.id !== "Vendor",
    })

    useEffect(() => {
        return () => {
            if (props?.isShown && !editFlag) resetAddAccountData()
        }
    }, [props?.isShown, editFlag])

    return (
        <>
            <div className={`${styles.editProfile_modal}`}>
                <form
                    className={`${styles.editProfile_modal__form}`}
                    onSubmit={(e: any) => {
                        addfunctions.onSubmit(e)
                    }}>
                    <Box
                        className={styles.responsive_grid_2col}
                        display="grid"
                        gridTemplateColumns="repeat(2, minmax(0, 1fr))"
                        gap={2}>
                        <Box>
                            <Box display={"flex"} alignItems={"center"}>
                                <Typography variant="label">Account Type*</Typography>
                                {addStates.toggleError && !addStates?.addAccountFormValue?.account_type && (
                                    <FontAwesomeIcon icon={faCircleExclamation} color="#F94054" size="xs" />
                                )}
                            </Box>
                            <RadioGroup
                                options={[
                                    { id: "Client", title: "Client" },
                                    { id: "Vendor", title: "Vendor" },
                                ]}
                                disabled={!addStates?.addAllAccountsPermission || disableAccountType}
                                onSelect={(e: any) => handleAccountTypeChange(e)}
                                labelsStyle={{ fontSize: "12px", marginTop: 10 }}
                                value={addStates?.addAccountFormValue?.account_type}
                            />
                        </Box>

                        <Box>
                            <Typography variant="label">Account Name*</Typography>
                            <InputTag
                                name="account_name"
                                value={addStates.addAccountFormValue.account_name}
                                error={!addStates.addAccountFormValue.account_name}
                                showError={addStates.toggleError}
                                onChange={(e: any) => addfunctions.changeValues(e)}
                                placeholder="Enter Account name"
                                required
                            />
                        </Box>

                        <Box>
                            <Typography variant="label">Legal Business Name*</Typography>
                            <InputTag
                                name="legal_business_name"
                                value={addStates.addAccountFormValue.legal_business_name}
                                error={!addStates.addAccountFormValue.legal_business_name}
                                showError={addStates.toggleError}
                                onChange={(e: any) => addfunctions.changeValues(e)}
                                placeholder="Enter Legal Business name"
                                required
                            />
                        </Box>
                        <Box display="flex" flexDirection="column" justifyContent="space-between">
                            <Box className={`${styles.checkboxes}`} justifyContent="flex-start">
                                <Checkbox
                                    onChange={() => {
                                        addfunctions?.handleToggle()
                                    }}
                                    disabled={addStates?.addAccountFormValue?.account_type?.id === "Vendor"}
                                    checked={
                                        addStates?.isChecked &&
                                        addStates?.addAccountFormValue?.account_type?.id !== "Vendor"
                                    }
                                />
                                <span
                                    className={`${styles.editProfile_modal__form__flex__label}`}
                                    style={{ margin: "0px 10px" }}>
                                    Link with Parent Account
                                </span>
                            </Box>
                            <MultiSelectDropdown
                                title="Select Account"
                                options={addStates?.getLinkedAccountData?.map((data: any) => {
                                    return { id: data?.id, title: data?.account_name }
                                })}
                                id="account_id"
                                onChange={() => {}}
                                selectedOptions={
                                    addStates.addAccountFormValue.account_id?.id &&
                                    addStates?.addAccountFormValue?.account_type?.id !== "Vendor"
                                        ? [
                                              {
                                                  id: addStates.addAccountFormValue.account_id.id,
                                                  title: addStates.addAccountFormValue.account_id.title,
                                              },
                                          ]
                                        : []
                                }
                                selectionType={"single"}
                                disabled={
                                    !addStates?.isChecked ||
                                    addStates?.addAccountFormValue?.account_type?.id === "Vendor"
                                }
                                onSelect={(e: any) => addfunctions.changeAccountHandle(e)}
                                error={!addStates.addAccountFormValue.account_id?.id}
                                showError={addStates?.isChecked && addStates.toggleError}
                            />
                        </Box>

                        {/* A client gives its Zoho customer ID here, a vendor its Ref ID (BUGS-612 / 615). */}
                        <Box>
                            <Typography variant="label">{identifierField.label}</Typography>
                            <InputTag
                                key={identifierField.name}
                                name={identifierField.name}
                                inputMode="numeric"
                                value={addStates.addAccountFormValue[identifierField.name] ?? ""}
                                onChange={(e: any) => addfunctions.changeValues(e)}
                                placeholder={identifierField.placeholder}
                            />
                            {identifierField.name === "zoho_contact_id" && addStates.zohoIdErrorText ? (
                                <Typography color="error" fontSize="12px" mt="4px" role="alert">
                                    {addStates.zohoIdErrorText}
                                </Typography>
                            ) : null}
                        </Box>
                    </Box>
                    {addStates?.addAccountFormValue?.account_type?.id === "Vendor" && (
                        <>
                            <div className={`${styles.headings} mb-10 mt-15`}>Vendor Details</div>
                            <Box display="flex" flexDirection="column" gap={2}>
                                <Box
                                    className={styles.responsive_grid_2col}
                                    display="grid"
                                    gridTemplateColumns="repeat(2, minmax(0, 1fr))"
                                    gap={2}>
                                    <Box>
                                        <Typography variant="label">
                                            Services*
                                            {!addStates?.vendorDetails?.services && addStates?.toggleError && (
                                                <img src={error_mark} alt="error" />
                                            )}
                                        </Typography>
                                        <MultiSelectDropdown
                                            options={serviceData?.map((data: any) => {
                                                return { id: data?.category_id, title: data?.service_name }
                                            })}
                                            title="Select Services"
                                            onSelect={(value: any) =>
                                                setVendorDetails({ ...addStates?.vendorDetails, services: value })
                                            }
                                            selectedOptions={addStates?.vendorDetails?.services}
                                            selectionType="multiple"
                                            error={addStates.vendorDetails.services?.length < 1}
                                            showError={addStates.toggleError}
                                        />
                                    </Box>
                                    <Box>
                                        <Typography variant="label">
                                            Department*{" "}
                                            {!addStates?.vendorDetails?.department && addStates?.toggleError && (
                                                <img src={error_mark} alt="error" />
                                            )}
                                        </Typography>
                                        <MultiSelectDropdown
                                            options={departmentData?.map((data: any, index: number) => ({
                                                id: data,
                                                title: data,
                                            }))}
                                            title="Select department"
                                            onSelect={(value: any) =>
                                                setVendorDetails({ ...addStates?.vendorDetails, department: value })
                                            }
                                            selectedOptions={addStates?.vendorDetails?.department}
                                            selectionType="multiple"
                                            error={addStates.vendorDetails.department?.length < 1}
                                            showError={addStates.toggleError}
                                        />
                                    </Box>
                                    <Box>
                                        <Typography variant="label">Zone</Typography>
                                        <MultiSelectDropdown
                                            options={ZONE_OPTIONS}
                                            title="Select zone"
                                            onSelect={(value: any) =>
                                                setVendorDetails({ ...addStates?.vendorDetails, zone: value })
                                            }
                                            selectedOptions={addStates?.vendorDetails?.zone}
                                            selectionType="multiple"
                                        />
                                    </Box>
                                    <Box>
                                        <Typography variant="label">State</Typography>
                                        <MultiSelectDropdown
                                            hideSelectAll
                                            options={addStates?.vendorStateList?.map((state: any) => ({
                                                id: state?.id,
                                                title: state?.name,
                                            }))}
                                            title="Select State"
                                            onSelect={(value: any) =>
                                                setVendorDetails({ ...addStates?.vendorDetails, state: value })
                                            }
                                            selectedOptions={addStates?.vendorDetails?.state}
                                            disabled={addStates?.vendorDetails?.zone?.length < 1}
                                            selectionType="multiple"
                                        />
                                    </Box>
                                    <Box>
                                        <Typography variant="label">City</Typography>

                                        <MultiSelectDropdown
                                            options={addStates?.vendorCitiesList?.map((cities: any) => ({
                                                id: cities?.id,
                                                title: cities?.name,
                                            }))}
                                            title="Select City"
                                            onSelect={(value: any) =>
                                                setVendorDetails({ ...addStates?.vendorDetails, city: value })
                                            }
                                            disabled={addStates?.vendorDetails?.state?.length < 1}
                                            selectedOptions={addStates?.vendorDetails?.city}
                                            selectionType="multiple"
                                        />
                                    </Box>
                                    <Box>
                                        <Typography variant="label">Pan</Typography>
                                        <Box display="flex" flexDirection="column" gap="1px">
                                            <InputTag
                                                name="pan"
                                                value={
                                                    addStates.vendorDetails.pan
                                                        ? addStates.vendorDetails.pan.toUpperCase()
                                                        : ""
                                                }
                                                onChange={(e: any) =>
                                                    setVendorDetails({
                                                        ...addStates?.vendorDetails,
                                                        pan: e.target.value.toUpperCase(),
                                                    })
                                                }
                                                error={!!addStates.vendorDetails.pan && !addfunctions.isPanValid()}
                                                maxCharacter={10}
                                                showError={addStates.toggleError}
                                                placeholder="Enter Pan"
                                                required
                                            />
                                            {!!addStates.vendorDetails.pan &&
                                                !addfunctions.isPanValid() &&
                                                addStates.toggleError && (
                                                    <Typography variant="caption" fontSize="10px" color="#f94054">
                                                        {validationError.PAN}
                                                    </Typography>
                                                )}
                                        </Box>
                                    </Box>
                                </Box>
                                <Box
                                    className={styles.responsive_grid_3col}
                                    display="grid"
                                    gridTemplateColumns="1fr 1fr 1fr"
                                    gap={2}>
                                    <Box>
                                        <Typography variant="label">MSME Registration</Typography>
                                        <RadioGroup
                                            options={[
                                                { id: "1", title: "Yes" },
                                                { id: "0", title: "No" },
                                            ]}
                                            onSelect={(e: any) =>
                                                setVendorDetails({ ...addStates?.vendorDetails, msme_reg: e })
                                            }
                                            labelsStyle={{ fontSize: "12px", marginTop: 10 }}
                                            value={addStates?.vendorDetails?.msme_reg}
                                        />
                                    </Box>
                                    {addStates?.vendorDetails?.msme_reg?.id === "1" && (
                                        <>
                                            <Box>
                                                <Typography variant="label">MSME Registration Type*</Typography>
                                                <MultiSelectDropdown
                                                    options={MSME_REG_TYPE_OPTIONS}
                                                    title="Select Type"
                                                    onSelect={(value: any) =>
                                                        setVendorDetails({
                                                            ...addStates?.vendorDetails,
                                                            msme_reg_type: value,
                                                        })
                                                    }
                                                    selectedOptions={
                                                        addStates?.vendorDetails?.msme_reg_type.id
                                                            ? [
                                                                  {
                                                                      id: addStates?.vendorDetails?.msme_reg_type.id,
                                                                      title: addStates?.vendorDetails?.msme_reg_type
                                                                          .title,
                                                                  },
                                                              ]
                                                            : []
                                                    }
                                                    selectionType="single"
                                                    error={!addStates.vendorDetails.msme_reg_type?.id}
                                                    showError={addStates.toggleError}
                                                />
                                            </Box>
                                            <Box>
                                                <Typography variant="label">MSME Registration Number*</Typography>
                                                <InputTag
                                                    name="msme_reg_number"
                                                    placeholder="Enter Number"
                                                    value={addStates.vendorDetails.msme_reg_number}
                                                    onChange={(e: any) =>
                                                        setVendorDetails({
                                                            ...addStates?.vendorDetails,
                                                            msme_reg_number: e.target.value,
                                                        })
                                                    }
                                                    error={!addStates.vendorDetails.msme_reg_number}
                                                    showError={addStates.toggleError}
                                                />
                                            </Box>
                                        </>
                                    )}
                                </Box>
                                <Box width="50%">
                                    <Typography variant="label">GST Treatment</Typography>
                                    <MultiSelectDropdown
                                        options={GST_TREATMENT_OPTIONS}
                                        title="Select Treatment"
                                        onSelect={(value: any) =>
                                            setVendorDetails({ ...addStates?.vendorDetails, gst_treatment: value })
                                        }
                                        selectedOptions={
                                            addStates?.vendorDetails?.gst_treatment.id
                                                ? [
                                                      {
                                                          id: addStates?.vendorDetails?.gst_treatment.id,
                                                          title: addStates?.vendorDetails?.gst_treatment.title,
                                                      },
                                                  ]
                                                : []
                                        }
                                        selectionType="single"
                                    />
                                </Box>
                            </Box>
                        </>
                    )}
                    <Box display={"flex"} marginTop={2} justifyContent={"space-between"} alignItems={"center"}>
                        <Box className={styles.headings}>LOCATION DETAILS</Box>
                        <Box
                            onClick={() => addfunctions.addFormFields()}
                            sx={{
                                cursor: "pointer",
                                color: "#0430C0",
                                fontSize: "10px",
                                fontWeight: 500,
                                textDecoration: "underline",
                            }}>
                            +Add Billing Address
                        </Box>
                    </Box>
                    <ShippingAddress addStates={addStates} addfunctions={addfunctions} />
                    <Box display={"flex"} marginTop={2} justifyContent={"space-between"} alignItems={"center"}>
                        <Box className={styles.headings}>CONTACT DETAILS</Box>
                        <Box
                            onClick={() => addfunctions.addContactFields()}
                            sx={{
                                cursor: "pointer",
                                color: "#0430C0",
                                fontSize: "10px",
                                fontWeight: 500,
                                textDecoration: "underline",
                            }}>
                            +Add Contact
                        </Box>
                    </Box>
                    <ContactDetails addStates={addStates} addfunctions={addfunctions} />

                    <Box display={"flex"} marginTop={2} justifyContent={"space-between"} alignItems={"center"}>
                        <Box className={styles.headings}>BANK DETAILS</Box>
                        <Box
                            onClick={() => addfunctions.addBankDetails()}
                            sx={{
                                cursor: "pointer",
                                color: "#0430C0",
                                fontSize: "10px",
                                fontWeight: 500,
                                textDecoration: "underline",
                            }}>
                            +Add Bank Details
                        </Box>
                    </Box>
                    <BankDetails addStates={addStates} addfunctions={addfunctions} />
                </form>
            </div>
            {addStates?.toggleError &&
                ((handleDisable() && !areAllContactFieldsFilled()) || !areAllBillingFieldsFilled()) && (
                    <div className={styles.error_text}>Please fill the fields*</div>
                )}

            <div className={`${styles.form_footer}`}>
                <div className={`${styles.form_footer_button} ${styles.cancel}`} onClick={props.toggle}>
                    Cancel
                </div>
                <div
                    className={`${styles.form_footer_button} ${styles.save}`}
                    onClick={(e: any) => {
                        addfunctions.onSubmit(e)
                    }}>
                    {addStates.isAccountLoading ? <CircularLoader hide={!addStates.isAccountLoading} /> : "Save"}
                </div>
            </div>
        </>
    )
}

export default AddAccounts
